import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, refreshSession, setAccessToken, setAuthHandlers, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = signed out
  const [reauthOpen, setReauthOpen] = useState(false);
  const resolver = useRef(null);

  const signOutLocal = useCallback(() => { setAccessToken(null); setUser(false); }, []);

  const requestReauth = useCallback(() => new Promise((resolve) => {
    resolver.current = resolve;
    setReauthOpen(true);
  }), []);

  useEffect(() => {
    setAuthHandlers({ onUser: setUser, onLogout: signOutLocal, onReauth: requestReauth });
    refreshSession().then((d) => setUser(d.user)).catch(() => setUser(false));
  }, [signOutLocal, requestReauth]);

  const completeLogin = (data) => { setAccessToken(data.access_token); setUser(data.user); };
  const reloadUser = async () => { const { data } = await api.get("/auth/me"); setUser(data); return data; };
  const logout = async () => { try { await api.post("/auth/logout"); } catch (e) { /* session already gone */ } signOutLocal(); };
  const can = (perm) => !!user && user.permissions?.includes(perm);

  const finishReauth = (ok) => { setReauthOpen(false); resolver.current?.(ok); resolver.current = null; };

  return (
    <AuthCtx.Provider value={{ user, setUser, completeLogin, reloadUser, logout, can, requestReauth }}>
      {children}
      <ReauthDialog open={reauthOpen} onDone={finishReauth} />
    </AuthCtx.Provider>
  );
}

function ReauthDialog({ open, onDone }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await api.post("/auth/reauth", { password: pw }); setPw(""); onDone(true); }
    catch (ex) { setErr(errMsg(ex)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onDone(false)}>
      <DialogContent data-testid="reauth-dialog">
        <DialogHeader>
          <DialogTitle>Confirm it's you</DialogTitle>
          <DialogDescription>This security change requires recent authentication. Enter your password.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" data-testid="reauth-password-input" />
          {err && <p className="text-sm text-rose-600" data-testid="reauth-error">{err}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onDone(false)}>Cancel</Button>
            <Button type="submit" disabled={busy || !pw} data-testid="reauth-submit-button">Confirm</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
